import { useState, useEffect } from 'react';
import { DetailCard } from '@/components/common/DetailCard';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { updateJob } from '../api/jobs-api';
import { getSubcontractorLookup } from '@/features/subcontractors/api/subcontractors';
import type { SubcontractorLookupDto } from '@/features/subcontractors/types';

interface SubcontractorAssignmentProps {
  jobId: number;
  currentSubcontractorId?: number;
  currentSubcontractorName?: string;
  onUpdate: () => void;
}

export function SubcontractorAssignment({
  jobId,
  currentSubcontractorId,
  currentSubcontractorName,
  onUpdate,
}: SubcontractorAssignmentProps) {
  const [subcontractors, setSubcontractors] = useState<SubcontractorLookupDto[]>([]);
  const [selectedId, setSelectedId] = useState<number | undefined>(currentSubcontractorId);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    getSubcontractorLookup()
      .then(setSubcontractors)
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    setSelectedId(currentSubcontractorId);
  }, [currentSubcontractorId]);

  const handleChange = async (value: string) => {
    const newId = value ? Number(value) : undefined;
    if (newId === selectedId) return;

    setSelectedId(newId);
    setIsUpdating(true);
    setError(null);

    try {
      await updateJob(jobId, { subcontractorId: newId ?? null });
      onUpdate();
    } catch {
      setSelectedId(currentSubcontractorId);
      setError('Failed to update subcontractor');
    } finally {
      setIsUpdating(false);
    }
  };

  const hasOptions = subcontractors.length > 0;
  const showCurrentAsText = !hasOptions && !loadError && currentSubcontractorName;

  return (
    <DetailCard title="Subcontractor">
      <div className="space-y-2">
        <Label htmlFor="subcontractor-select">Assigned Subcontractor</Label>
        {showCurrentAsText ? (
          <p className="text-sm">{currentSubcontractorName}</p>
        ) : (
          <Select
            id="subcontractor-select"
            value={selectedId?.toString() || ''}
            onChange={(e) => handleChange(e.target.value)}
            disabled={isUpdating || (!hasOptions && !loadError)}
          >
            <option value="">None</option>
            {subcontractors.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.companyName}
              </option>
            ))}
          </Select>
        )}
        {loadError && (
          <p className="text-xs text-red-600">Failed to load subcontractors</p>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </DetailCard>
  );
}
